// EventsPage – events overview with filters, details, and admin dialogs.

import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { EventFiltersValue } from '../components/EventsManagementView/types/EventsOverviewTypes';
import {
  AssignOperatorDialog,
  CreateEventDialog,
  OperatorIntroDialog,
} from '../components/EventsManagementView/dialogs';
import { EventsOverviewMainPanel } from '../components/EventsManagementView/panels';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  DevicePackagingToDistributionConfirmDialog,
  DraftToDevicePackagingConfirmDialog,
  ActiveToCompleteConfirmDialog,
  ActiveToReportingConfirmDialog,
  ReportingToCompleteConfirmDialog,
  DistributionToRackingConfirmDialog,
  RackingToActiveConfirmDialog,
  CombinedDistributionToActiveConfirmDialog,
} from '../components/EventsManagementView/dialogs';
import DevicePackageAssignmentDialog from '../components/EventsManagementView/panels/DevicePackageAssignmentDialog';
import RefreshButton from '../components/shared/RefreshButton';
import { PAGE_TAB_HEADER } from '../components/shared/ui';
import { PortaledLayer } from '../components/shared/PortaledLayer';
import BarcodeScannerEntryField from '../components/shared/BarcodeScannerEntryField';
import InlineSpinner from '../components/shared/InlineSpinner';
import useBarcodeScanner from '../components/shared/useBarcodeScanner';
import { maybeConvertRacerangerScan } from '../helpers/racerangerDeviceScan';
import {
  CreateEventFormState,
  EventRaceSummaryRow,
  EventRow,
  EventUserOption,
  EventWithDetails,
  WorldTriathlonEventOption,
} from '../components/EventsManagementView/types';
import {
  BTN_ICON,
  BTN_PRIMARY,
  BTN_SECONDARY,
  buildGlobalUserIdToDisplayName,
  dispatchToast,
  dispatchToastError,
  formatApiResponseError,
  fetchAllEventDevicesRaw,
  formatMoveToStatusLabel,
  getNextRaceStatus,
  getPrevRaceStatus,
  isAllowedRaceStatusTransition,
  isCombinedModeRace,
  isEventDeletedOnly,
  isRaceAssignmentsLocked,
  normalizeRaceStatus,
  toMs,
  toUnixSeconds,
} from '../components/EventsManagementView/utils';
import { useAuth } from '../context/AuthContext';
import { formatTs, formatUserIdentityDisplay } from '../helpers/helpers';
import { readValidationReportViewed } from '../components/EventsManagementView/utils/raceDetailPortalHelpers';

type EventsOverviewPageProps = {
  operatorActiveEventId?: string | null;
  onOperatorEventContinue?: (event: EventRow) => void;
  onOperatorEventClear?: () => void;
  /** Operator: App sets active event (and optional race) then navigates to Devices. */
  onOperatorNavigateToDevices?: (payload: {
    eventId: string;
    eventName?: string | null;
    raceId?: string | null;
    unretrievedOnly?: boolean;
  }) => void;
};

export default function EventsOverviewPage({
  operatorActiveEventId = null,
  onOperatorEventContinue,
  onOperatorEventClear,
  onOperatorNavigateToDevices,
}: EventsOverviewPageProps): JSX.Element {
  const { isAdmin, isOperator, userName } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState<EventRow[]>([]);
  const [details, setDetails] = useState<Record<string, EventWithDetails>>({});
  const [globalUserIdToDisplayName, setGlobalUserIdToDisplayName] = useState<Record<string, string>>({});
  const [availableUsers, setAvailableUsers] = useState<EventUserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshingDetailsByEventId, setRefreshingDetailsByEventId] = useState<Record<string, boolean>>({});
  const [eventDetailsRefreshVersionByEventId, setEventDetailsRefreshVersionByEventId] = useState<Record<string, number>>({});
  const [eventDetailsLoadedByEventId, setEventDetailsLoadedByEventId] = useState<Record<string, boolean>>({});

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [autoExpandId, setAutoExpandId] = useState<string | null>(null);
  const [operatorSelectedEventId, setOperatorSelectedEventId] = useState<string | null>(
    operatorActiveEventId,
  );
  const [operatorSelectionConfirmed, setOperatorSelectionConfirmed] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [assignOperatorDialogEventId, setAssignOperatorDialogEventId] = useState<string | null>(null);
  type RaceWorkflowDialogState = {
    eventId: string;
    raceId: string;
    kind: 'devicePackaging';
  } | null;
  const [raceWorkflowDialog, setRaceWorkflowDialog] = useState<RaceWorkflowDialogState>(null);
  const [recallDialogEventId, setRecallDialogEventId] = useState<string | null>(null);
  const [recallDialogRaceId, setRecallDialogRaceId] = useState<string | null>(null);
  const [recallScanValue, setRecallScanValue] = useState('');
  const [recallEntryMode, setRecallEntryMode] = useState<'scan' | 'manual'>('scan');
  const [recallingDeviceId, setRecallingDeviceId] = useState<string | null>(null);
  const [recallSubmitFlash, setRecallSubmitFlash] = useState<'success' | 'error' | null>(null);
  const recallSubmitFlashTimerRef = useRef<number | null>(null);
  const recallScanInputRef = useRef<HTMLInputElement | null>(null);
  const {
    connectionActive: recallScannerConnected,
    connectionPort: recallScannerPort,
    claim: claimRecallScanner,
  } = useBarcodeScanner({
    enabled: !!recallDialogEventId && recallEntryMode === 'scan',
    pollIntervalMs: 2500,
    mapScan: (raw) => maybeConvertRacerangerScan(raw),
    onScan: (value) => setRecallScanValue(value),
  });
  const [operatorIntroOpen, setOperatorIntroOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateEventFormState>({
    name: '',
    start: '',
    end: '',
    wtEventId: '',
  });
  const [wtEvents, setWtEvents] = useState<WorldTriathlonEventOption[]>([]);
  const [wtEventsLoading, setWtEventsLoading] = useState(false);
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'ADMIN' | 'OPERATOR'>('OPERATOR');
  const [assigningOperator, setAssigningOperator] = useState(false);
  const [progressingRaceKey, setProgressingRaceKey] = useState<string | null>(null);

  type RaceAdvanceOptions = {
    retrievedDeviceIds?: string[];
  };

  type RaceTransitionCheck = {
    key: string;
    label: string;
    ok: boolean;
    detail?: string;
  };

  type ConfirmState =
    | { kind: 'softDeleteEvent'; eventId: string }
    | { kind: 'hardDeleteEvent'; eventId: string }
    | { kind: 'removeOperator'; eventId: string; userId: string }
    | { kind: 'softDeleteRace'; eventId: string; raceId: string; raceName?: string }
    | { kind: 'hardDeleteRace'; eventId: string; raceId: string; raceName?: string }
    | {
      kind: 'advanceRaceDraftToDevicePackaging';
      eventId: string;
      raceId: string;
      raceName?: string;
      eventName?: string;
      nextStatus: string;
    }
    | {
      kind: 'advanceRacePackagingToDistribution';
      eventId: string;
      raceId: string;
      raceName?: string;
      eventName?: string;
      nextStatus: string;
    }
    | {
      kind: 'advanceRaceDistributionToRacking';
      eventId: string;
      raceId: string;
      raceName?: string;
      eventName?: string;
      nextStatus: string;
    }
    | {
      kind: 'advanceRaceRackingToActive';
      eventId: string;
      raceId: string;
      raceName?: string;
      eventName?: string;
      nextStatus: string;
      checks: RaceTransitionCheck[];
    }
    | {
      kind: 'advanceRaceCombinedDistributionToActive';
      eventId: string;
      raceId: string;
      raceName?: string;
      eventName?: string;
      nextStatus: string;
      rackedDeviceIds: string[];
      retrievalDeviceIds: string[];
      releasedDeviceIds: string[];
    }
    | {
      kind: 'advanceRaceActiveToReporting';
      eventId: string;
      raceId: string;
      raceName?: string;
      eventName?: string;
      nextStatus: string;
    }
    | {
      kind: 'advanceRaceReportingToComplete';
      eventId: string;
      raceId: string;
      raceName?: string;
      eventName?: string;
      nextStatus: string;
      nonRecalledDeviceIds: string[];
    }
    | {
      kind: 'revertRaceStatus';
      eventId: string;
      raceId: string;
      raceName?: string;
      currentStatus: string;
      targetStatus: string;
    }
    | null;
  const [confirmState, setConfirmState] = useState<ConfirmState>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const [filters, setFilters] = useState<EventFiltersValue>({
    search: '',
    status: 'any',
    startFrom: '',
    startTo: '',
    createdFrom: '',
    createdTo: '',
  });

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search.trim()) count += 1;
    if (filters.status !== 'any') count += 1;
    if (filters.startFrom) count += 1;
    if (filters.startTo) count += 1;
    if (filters.createdFrom) count += 1;
    if (filters.createdTo) count += 1;
    return count;
  }, [filters]);

  const loadAll = useCallback(async (): Promise<void> => {
    if (!window.eventsAPI) {
      dispatchToast('toast-error', 'Events API is not available');
      return;
    }

    setLoading(true);

    try {
      const usersPromise = isAdmin
        ? window.eventsAPI.listUsers()
        : Promise.resolve({ ok: false as const, status: 403 });

      const [eventsRes, usersRes] = await Promise.all([
        isAdmin ? window.eventsAPI.listEvents({ limit: 200 }) : window.eventsAPI.listMyEvents(),
        usersPromise,
      ]);

      if (usersRes.ok) {
        const ud = usersRes.data as { users?: unknown[] } | unknown[] | undefined;
        const users = Array.isArray(ud) ? ud : Array.isArray(ud?.users) ? ud.users : [];
        setGlobalUserIdToDisplayName(buildGlobalUserIdToDisplayName(users));

        const mappedUsers: EventUserOption[] = users
          .filter((raw): raw is Record<string, unknown> => !!raw && typeof raw === 'object')
          .map((u) => ({
            userId:
              (typeof u.userId === 'string' && u.userId.trim()) ||
              (typeof u.sub === 'string' && u.sub.trim()) ||
              '',
            preferred_username:
              typeof u.preferred_username === 'string' ? u.preferred_username.trim() : '',
            username: typeof u.username === 'string' ? u.username.trim() : '',
            email: typeof u.email === 'string' ? u.email.trim() : '',
          }))
          .filter((u) => !!u.userId);
        setAvailableUsers(mappedUsers);
      } else {
        setGlobalUserIdToDisplayName({});
        setAvailableUsers([]);
      }

      if (!eventsRes.ok) {
        const msg = `Failed to load events (${eventsRes.status ?? 'error'})`;
        dispatchToast('toast-error', msg);
        setEvents([]);
        setDetails({});
        setExpanded({});
        return;
      }

      const data = eventsRes.data as { events?: EventRow[] };
      const rows: EventRow[] = Array.isArray(data?.events) ? data.events : [];

      if (!rows.length) {
        setEvents([]);
        setDetails({});
        setExpanded({});
        return;
      }

      const map: Record<string, EventWithDetails> = {};
      const exp: Record<string, boolean> = {};
      const loaded: Record<string, boolean> = {};
      for (const ev of rows) {
        map[ev.eventId] = {
          event: ev,
          devices: [],
          operators: [],
          races: [],
        };
        exp[ev.eventId] = false;
        loaded[ev.eventId] = false;
      }

      setEvents(rows);
      setDetails(map);
      setExpanded(exp);
      setEventDetailsLoadedByEventId(loaded);
    } catch (err: unknown) {
      dispatchToastError(err, 'Failed to load events overview');
      setEvents([]);
      setDetails({});
      setExpanded({});
      setEventDetailsLoadedByEventId({});
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!selectedUserId && availableUsers.length > 0) {
      setSelectedUserId(availableUsers[0].userId);
    }
  }, [availableUsers, selectedUserId]);

  useEffect(() => {
    if (isAdmin) {
      setOperatorSelectionConfirmed(false);
      setOperatorSelectedEventId(null);
      return;
    }

    if (!events.length) {
      setOperatorSelectionConfirmed(false);
      setOperatorSelectedEventId(null);
      onOperatorEventClear?.();
      return;
    }

    if (
      operatorSelectedEventId &&
      !events.some((event) => event.eventId === operatorSelectedEventId)
    ) {
      setOperatorSelectedEventId(null);
      setOperatorSelectionConfirmed(false);
      onOperatorEventClear?.();
    }
  }, [events, isAdmin, onOperatorEventClear, operatorSelectedEventId]);

  useEffect(() => {
    if (isAdmin) return;
    setOperatorSelectedEventId(operatorActiveEventId);
    setOperatorSelectionConfirmed(!!operatorActiveEventId);
  }, [isAdmin, operatorActiveEventId]);

  useEffect(() => {
    if (isAdmin || !isOperator) {
      setOperatorIntroOpen(false);
      return;
    }

    if (operatorSelectionConfirmed) {
      setOperatorIntroOpen(false);
      return;
    }

    setOperatorIntroOpen(true);
  }, [isAdmin, isOperator, operatorSelectionConfirmed]);

  useEffect(() => {
    function handleOpen(ev: Event): void {
      const ce = ev as CustomEvent<{ eventId?: string } | string>;
      const detail = ce.detail;
      const eventId = typeof detail === 'string' ? detail : detail?.eventId;
      if (!eventId) return;
      setAutoExpandId(eventId);
    }

    window.addEventListener('open-event-details', handleOpen as EventListener);
    return () => {
      window.removeEventListener('open-event-details', handleOpen as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!autoExpandId || !events.length) return;
    const exists = events.some((e) => e.eventId === autoExpandId);
    if (!exists) return;

    setExpanded((prev) => {
      const next: Record<string, boolean> = { ...prev };
      for (const id of Object.keys(next)) next[id] = false;
      next[autoExpandId] = true;
      return next;
    });

    const el = document.getElementById(`event-row-${autoExpandId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [autoExpandId, events]);

  function toggleExpanded(eventId: string): void {
    setExpanded((prev) => ({
      ...prev,
      [eventId]: !prev[eventId],
    }));
  }

  const refreshEventDetails = useCallback(async (eventId: string, raceId?: string): Promise<void> => {
    if (!window.eventsAPI) {
      dispatchToast('toast-error', 'Events API is not available');
      return;
    }

    const id = String(eventId || '').trim();
    if (!id) return;

    const scopedRaceId = String(raceId || '').trim();

    // A scan inside one race only ever changes that race's own devices — operators and the
    // race list are unaffected, so skip refetching them and scope the device refetch to just
    // this race instead of paging through every device in the whole event.
    if (scopedRaceId) {
      setRefreshingDetailsByEventId((prev) => ({ ...prev, [id]: true }));
      try {
        const devsRes = await fetchAllEventDevicesRaw(id, { raceId: scopedRaceId });
        if (!devsRes.ok) {
          dispatchToast('toast-error', `Failed to refresh devices for race ${scopedRaceId}`);
          return;
        }
        const scopedDevices = (devsRes.devices as any[]) || [];
        setDetails((prev) => {
          const existing = prev[id];
          if (!existing) return prev;
          const untouched = (existing.devices ?? []).filter((d: any) => String(d?.raceId ?? '').trim() !== scopedRaceId);
          return {
            ...prev,
            [id]: {
              ...existing,
              devices: [...untouched, ...scopedDevices],
            },
          };
        });
        setEventDetailsRefreshVersionByEventId((prev) => ({
          ...prev,
          [id]: (prev[id] ?? 0) + 1,
        }));
      } catch (err: unknown) {
        dispatchToastError('Failed to refresh race devices', err, `Failed to refresh devices for race ${scopedRaceId}`);
      } finally {
        setRefreshingDetailsByEventId((prev) => ({ ...prev, [id]: false }));
      }
      return;
    }

    setRefreshingDetailsByEventId((prev) => ({ ...prev, [id]: true }));
    try {
      const [devsRes, opsRes, racesRes] = await Promise.all([
        fetchAllEventDevicesRaw(id),
        window.eventsAPI.listEventOperators(id),
        window.eventsAPI.listEventRaces(id),
      ]);

      const raceRowsRaw = racesRes.ok
        ? (racesRes.data as { races?: unknown[] } | undefined)?.races
        : undefined;
      const races = Array.isArray(raceRowsRaw)
        ? raceRowsRaw
          .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
          .map((r) => ({
            raceId: String(r.raceId ?? '').trim(),
            name: r.name != null ? String(r.name) : undefined,
            status: r.status != null ? String(r.status) : undefined,
            combinedMode: isCombinedModeRace(r as { combinedMode?: unknown }),
            createdAt: typeof r.createdAt === 'number' ? r.createdAt : undefined,
            wtProgramId: r.wtProgramId != null ? String(r.wtProgramId) : undefined,
            wtProgramName: r.wtProgramName != null ? String(r.wtProgramName) : undefined,
            latestAthletesFileId:
              r.latestAthletesFileId != null ? String(r.latestAthletesFileId).trim() : undefined,
          }))
          .filter((r) => r.raceId)
        : [];

      setDetails((prev) => {
        const existing = prev[id];
        if (!existing) return prev;
        return {
          ...prev,
          [id]: {
            ...existing,
            devices: devsRes.ok ? ((devsRes.devices as any[]) || []) : [],
            operators: opsRes.ok ? ((opsRes.data as { operators?: unknown[] })?.operators as any[]) || [] : [],
            races: racesRes.ok ? races : existing.races ?? [],
          },
        };
      });
      setEventDetailsLoadedByEventId((prev) => ({ ...prev, [id]: true }));
      setEventDetailsRefreshVersionByEventId((prev) => ({
        ...prev,
        [id]: (prev[id] ?? 0) + 1,
      }));
    } catch (err: unknown) {
      dispatchToastError('Failed to refresh event details', err, `Failed to refresh event details (${id})`);
    } finally {
      setRefreshingDetailsByEventId((prev) => ({ ...prev, [id]: false }));
    }
  }, []);

  useEffect(() => {
    const idsToLoad = Object.keys(expanded).filter((id) => (
      expanded[id]
      && !eventDetailsLoadedByEventId[id]
      && !refreshingDetailsByEventId[id]
    ));
    if (!idsToLoad.length) return;
    idsToLoad.forEach((id) => {
      void refreshEventDetails(id);
    });
  }, [eventDetailsLoadedByEventId, expanded, refreshingDetailsByEventId, refreshEventDetails]);

  const patchEventRow = useCallback((eventId: string, patch: Partial<EventRow>): void => {
    const id = String(eventId || '').trim();
    if (!id) return;
    setEvents((prev) => prev.map((ev) => (ev.eventId === id ? { ...ev, ...patch } : ev)));
  }, []);

  const removeEventRowLocally = useCallback((eventId: string): void => {
    const id = String(eventId || '').trim();
    if (!id) return;
    setEvents((prev) => prev.filter((ev) => ev.eventId !== id));
    setDetails((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setExpanded((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  function openAssignOperatorDialog(eventId: string): void {
    if (!eventDetailsLoadedByEventId[eventId] && !refreshingDetailsByEventId[eventId]) {
      void refreshEventDetails(eventId);
    }
    setAssignOperatorDialogEventId(eventId);
    setSelectedRole('OPERATOR');
    const assignedUserIds = new Set((details[eventId]?.operators ?? []).map((operator) => operator.userId));
    const firstAvailableUser =
      availableUsers.find((user) => !assignedUserIds.has(user.userId)) ?? availableUsers[0];
    setSelectedUserId(firstAvailableUser?.userId ?? '');
  }

  function matchesFilters(ev: EventRow): boolean {
    const q = filters.search.trim().toLowerCase();
    if (q) {
      const name = String(ev.name ?? '').toLowerCase();
      const id = String(ev.eventId ?? '').toLowerCase();
      if (!name.includes(q) && !id.includes(q)) return false;
    }

    if (filters.status !== 'any') {
      const st = String(ev.status ?? 'UNKNOWN').toUpperCase();
      if (st !== String(filters.status).toUpperCase()) return false;
    }

    const startMs = toMs(ev.startAt);
    if (filters.startFrom) {
      const fromMs = Date.parse(filters.startFrom);
      if (!Number.isNaN(fromMs) && (!startMs || startMs < fromMs)) return false;
    }
    if (filters.startTo) {
      const toMs2 = Date.parse(filters.startTo);
      if (!Number.isNaN(toMs2) && (!startMs || startMs > toMs2)) return false;
    }

    const createdMs = toMs(ev.createdAt);
    if (filters.createdFrom) {
      const fromMs = Date.parse(filters.createdFrom);
      if (!Number.isNaN(fromMs) && (!createdMs || createdMs < fromMs)) return false;
    }
    if (filters.createdTo) {
      const toMs2 = Date.parse(filters.createdTo);
      if (!Number.isNaN(toMs2) && (!createdMs || createdMs > toMs2)) return false;
    }

    return true;
  }

  const filteredEvents = useMemo(
    () => events.filter(matchesFilters),
    [events, filters],
  );

  const visibleEvents = useMemo(() => {
    if (isOperator && !isAdmin && operatorSelectionConfirmed && operatorSelectedEventId) {
      return filteredEvents.filter((event) => event.eventId === operatorSelectedEventId);
    }
    return filteredEvents;
  }, [
    filteredEvents,
    isAdmin,
    isOperator,
    operatorSelectionConfirmed,
    operatorSelectedEventId,
  ]);

  const operatorSelectedEvent = useMemo(
    () => events.find((event) => event.eventId === operatorSelectedEventId) ?? null,
    [events, operatorSelectedEventId],
  );

  const operatorSelectedEventLabel = useMemo(() => {
    if (!operatorSelectedEvent) return '';
    const eventName = String(operatorSelectedEvent.name ?? '').trim();
    return eventName
      ? `${eventName} (${operatorSelectedEvent.eventId})`
      : operatorSelectedEvent.eventId;
  }, [operatorSelectedEvent]);

  async function handleCreateEventSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();

    if (!window.eventsAPI) {
      dispatchToast('toast-error', 'Events API is not available');
      return;
    }

    if (!createForm.name.trim()) {
      dispatchToast('toast-error', 'Event name is required');
      return;
    }

    setCreatingEvent(true);
    try {
      const wtSel = createForm.wtEventId.trim();
      const wtEventName = wtSel
        ? wtEvents.find((w) => w.eventId === wtSel)?.title?.trim() || undefined
        : undefined;

      const res = await window.eventsAPI.createEvent({
        name: createForm.name.trim(),
        wtEventId: createForm.wtEventId || undefined,
        wtEventName,
        startAt: toUnixSeconds(createForm.start),
        endAt: toUnixSeconds(createForm.end),
      });

      if (!res.ok) {
        dispatchToast('toast-error', `Failed to create event: ${formatApiResponseError(res)}`);
        return;
      }

      setCreateForm({
        name: '',
        start: '',
        end: '',
        wtEventId: '',
      });
      setCreateDialogOpen(false);
      dispatchToast('toast-info', 'Event created successfully');
      await loadAll();
    } catch (err: any) {
      dispatchToastError(err, 'Failed to create event');
    } finally {
      setCreatingEvent(false);
    }
  }

  const loadWorldTriathlonEvents = useCallback(async (): Promise<void> => {
    if (!window.eventsAPI?.listWorldTriathlonEvents) return;
    setWtEventsLoading(true);
    try {
      const now = new Date();
      const from = `${now.getUTCFullYear()}-01-01`;
      const to = `${now.getUTCFullYear() + 1}-12-31`;
      const res = await window.eventsAPI.listWorldTriathlonEvents({
        categoryId: 351,
        startDate: from,
        endDate: to,
        perPage: 200,
      });
      if (!res?.ok) {
        dispatchToast('toast-error', `Failed to load WT events: ${formatApiResponseError(res)}`);
        return;
      }
      const rowsRaw = (res.data as { events?: unknown[] } | undefined)?.events;
      const rows = Array.isArray(rowsRaw)
        ? rowsRaw
          .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
          .map((r) => ({
            eventId: String(r.event_id ?? '').trim(),
            title: String(r.event_title ?? '').trim() || String(r.event_listing ?? '').trim() || String(r.event_id ?? '').trim(),
            date: typeof r.event_date === 'string' ? r.event_date : undefined,
            countryNoc: typeof r.event_country_noc === 'string' ? r.event_country_noc : undefined,
          }))
          .filter((r) => r.eventId)
        : [];
      setWtEvents(rows);
    } catch (err: any) {
      dispatchToastError(err, 'Failed to load WT events');
    } finally {
      setWtEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!createDialogOpen) return;
    if (wtEvents.length > 0 || wtEventsLoading) return;
    void loadWorldTriathlonEvents();
  }, [createDialogOpen, loadWorldTriathlonEvents, wtEvents.length, wtEventsLoading]);

  async function handleAssignOperator(): Promise<void> {
    if (!window.eventsAPI) {
      dispatchToast('toast-error', 'Events API is not available');
      return;
    }

    if (!assignOperatorDialogEventId || !selectedUserId) {
      dispatchToast('toast-error', 'Select a user to assign');
      return;
    }

    const assignedOperators = details[assignOperatorDialogEventId]?.operators ?? [];
    if (assignedOperators.some((operator) => operator.userId === selectedUserId)) {
      dispatchToast('toast-error', 'That user is already assigned to this event');
      return;
    }

    setAssigningOperator(true);
    try {
      const res = await window.eventsAPI.assignOperators(assignOperatorDialogEventId, [
        {
          userId: selectedUserId,
          roleInEvent: selectedRole,
        },
      ]);

      if (!res.ok) {
        dispatchToast('toast-error', `Failed to assign operator: ${formatApiResponseError(res)}`);
        return;
      }

      setAutoExpandId(assignOperatorDialogEventId);
      setAssignOperatorDialogEventId(null);
      dispatchToast('toast-info', 'Operator assigned successfully');
      await refreshEventDetails(assignOperatorDialogEventId);
    } catch (err: any) {
      dispatchToastError(err, 'Failed to assign operator');
    } finally {
      setAssigningOperator(false);
    }
  }

  async function buildRackingToActiveChecks(
    eventId: string,
    raceId: string,
  ): Promise<RaceTransitionCheck[]> {
    const checks: RaceTransitionCheck[] = [];

    const devsRes = await fetchAllEventDevicesRaw(eventId, { raceId });
    if (!devsRes.ok) {
      return [
        {
          key: 'unretrievedRacked',
          label: 'All non-retrieved devices are marked racked',
          ok: false,
          detail: 'Could not load race devices for racking checks',
        },
        {
          key: 'validationReviewed',
          label: 'Race validation report has been reviewed',
          ok: false,
          detail: 'Could not load race validation state',
        },
      ];
    }

    const raceDevices = (devsRes.devices as Record<string, unknown>[] | undefined) ?? [];
    const activeDevices = raceDevices.filter((d) => {
      const did = String(d?.deviceId ?? '').trim();
      if (!did) return false;
      return Number(d?.retrieved ?? d?.recalled ?? 0) !== 1;
    });
    const unracked = activeDevices
      .filter((d) => Number(d?.racked ?? 0) !== 1)
      .map((d) => String(d?.deviceId ?? '').trim())
      .filter(Boolean);
    checks.push({
      key: 'unretrievedRacked',
      label: 'All non-retrieved devices are marked racked',
      ok: unracked.length === 0,
      detail:
        unracked.length === 0
          ? undefined
          : `${unracked.length} device(s) are not racked (e.g. ${unracked.slice(0, 4).join(', ')}${unracked.length > 4 ? ', …' : ''}).`,
    });

    const validationReviewed = readValidationReportViewed(eventId, raceId);
    checks.push({
      key: 'validationReviewed',
      label: 'Race validation report has been reviewed',
      ok: validationReviewed,
      detail:
        validationReviewed
          ? undefined
          : 'Open the Validation report in the race workspace before moving to ACTIVE.',
    });

    return checks;
  }

  async function handleAdvanceRace(
    eventId: string,
    race: EventRaceSummaryRow,
    nextStatus: string,
    _options: RaceAdvanceOptions = {},
  ): Promise<void> {
    if (!window.eventsAPI) {
      dispatchToast('toast-error', 'Events API is not available');
      return;
    }

    const eid = String(eventId ?? '').trim();
    const raceId = String(race?.raceId ?? '').trim();
    if (!eid || !raceId) {
      dispatchToast('toast-error', 'Missing event or race ID');
      return;
    }

    const currentStatus = normalizeRaceStatus(race.status ?? 'DRAFT');
    const nextUpper = String(nextStatus).trim().toUpperCase();
    const eventName = events.find((ev) => ev.eventId === eid)?.name;
    const raceLabel = String(race.name ?? '').trim() || raceId;
    const combinedMode = isCombinedModeRace(race);
    if (!isAllowedRaceStatusTransition(currentStatus, nextUpper, { combinedMode })) {
      dispatchToast('toast-error', 'This race status change is not allowed');
      return;
    }

    const isBackward = getPrevRaceStatus(currentStatus, { combinedMode }) === nextUpper;
    if (isBackward) {
      setConfirmState({
        kind: 'revertRaceStatus',
        eventId: eid,
        raceId,
        raceName: raceLabel,
        currentStatus,
        targetStatus: nextUpper,
      });
      return;
    }

    if (currentStatus === 'DRAFT' && nextUpper === 'PACKAGING') {
      setConfirmState({
        kind: 'advanceRaceDraftToDevicePackaging',
        eventId: eid,
        raceId,
        raceName: raceLabel,
        eventName: eventName != null ? String(eventName) : undefined,
        nextStatus,
      });
      return;
    }

    if (currentStatus === 'PACKAGING' && nextUpper === 'DISTRIBUTION') {
      setConfirmState({
        kind: 'advanceRacePackagingToDistribution',
        eventId: eid,
        raceId,
        raceName: raceLabel,
        eventName: eventName != null ? String(eventName) : undefined,
        nextStatus,
      });
      return;
    }

    if (combinedMode && currentStatus === 'DISTRIBUTION' && nextUpper === 'ACTIVE') {
      setProgressingRaceKey(`${eid}#${raceId}`);
      try {
        const devsRes = await fetchAllEventDevicesRaw(eid, { raceId });
        if (!devsRes.ok) {
          dispatchToast(
            'toast-error',
            'Could not load devices for this race. Expand the event to load details, then try again.',
          );
          return;
        }
        const rows = (devsRes.devices as {
          deviceId?: string;
          recalled?: unknown;
          retrieved?: unknown;
          distributed?: unknown;
          racked?: unknown;
          assignmentStatus?: string;
        }[]) || [];
        const rackedDeviceIds: string[] = [];
        const retrievalDeviceIds: string[] = [];
        const releasedDeviceIds: string[] = [];
        for (const row of rows) {
          const did = String(row?.deviceId ?? '').trim();
          if (!did) continue;
          if (String(row?.assignmentStatus ?? '').trim().toUpperCase() === 'UNUSED') continue;
          if (Number(row?.retrieved ?? row?.recalled ?? 0) === 1) continue;
          if (Number(row?.racked ?? 0) === 1) rackedDeviceIds.push(did);
          else if (Number(row?.distributed ?? 0) === 1) retrievalDeviceIds.push(did);
          else releasedDeviceIds.push(did);
        }
        rackedDeviceIds.sort((a, b) => a.localeCompare(b));
        retrievalDeviceIds.sort((a, b) => a.localeCompare(b));
        releasedDeviceIds.sort((a, b) => a.localeCompare(b));
        setConfirmState({
          kind: 'advanceRaceCombinedDistributionToActive',
          eventId: eid,
          raceId,
          raceName: raceLabel,
          eventName: eventName != null ? String(eventName) : undefined,
          nextStatus: 'ACTIVE',
          rackedDeviceIds,
          retrievalDeviceIds,
          releasedDeviceIds,
        });
      } catch (err: unknown) {
        dispatchToastError(err, 'Failed to check device collection state');
      } finally {
        setProgressingRaceKey(null);
      }
      return;
    }

    if (currentStatus === 'DISTRIBUTION' && nextUpper === 'RACKING') {
      setConfirmState({
        kind: 'advanceRaceDistributionToRacking',
        eventId: eid,
        raceId,
        raceName: raceLabel,
        eventName: eventName != null ? String(eventName) : undefined,
        nextStatus,
      });
      return;
    }

    if (currentStatus === 'RACKING' && nextUpper === 'ACTIVE') {
      setProgressingRaceKey(`${eid}#${raceId}`);
      try {
        const checks = await buildRackingToActiveChecks(eid, raceId);
        setConfirmState({
          kind: 'advanceRaceRackingToActive',
          eventId: eid,
          raceId,
          raceName: raceLabel,
          eventName: eventName != null ? String(eventName) : undefined,
          nextStatus,
          checks,
        });
      } catch (err: unknown) {
        dispatchToastError(err, 'Failed to load ACTIVE transition checks');
      } finally {
        setProgressingRaceKey(null);
      }
      return;
    }

    if (currentStatus === 'ACTIVE' && nextUpper === 'REPORTING') {
      setConfirmState({
        kind: 'advanceRaceActiveToReporting',
        eventId: eid,
        raceId,
        raceName: raceLabel,
        eventName: eventName != null ? String(eventName) : undefined,
        nextStatus: 'REPORTING',
      });
      return;
    }

    if (currentStatus === 'REPORTING' && nextUpper === 'COMPLETE') {
      setProgressingRaceKey(`${eid}#${raceId}`);
      try {
        const devsRes = await fetchAllEventDevicesRaw(eid, { raceId });
        if (!devsRes.ok) {
          dispatchToast(
            'toast-error',
            'Could not load devices for this race. Expand the event to load details, then try again.',
          );
          return;
        }
        const devices = (devsRes.devices as { deviceId?: string; recalled?: unknown }[]) || [];
        const nonRecalledDeviceIds = devices
          .filter((d) => Number(d?.recalled ?? 0) !== 1)
          .map((d) => String(d?.deviceId || '').trim())
          .filter(Boolean);
        nonRecalledDeviceIds.sort((a, b) => a.localeCompare(b));
        setConfirmState({
          kind: 'advanceRaceReportingToComplete',
          eventId: eid,
          raceId,
          raceName: raceLabel,
          eventName: eventName != null ? String(eventName) : undefined,
          nextStatus: 'COMPLETE',
          nonRecalledDeviceIds,
        });
        return;
      } catch (err: unknown) {
        dispatchToastError(
          err,
          'Failed to check device retrieval state',
        );
        return;
      } finally {
        setProgressingRaceKey(null);
      }
    }

    setProgressingRaceKey(`${eid}#${raceId}`);
    try {
      const res = await window.eventsAPI.updateRaceStatus(eid, raceId, nextStatus);
      if (!res.ok) {
        dispatchToast('toast-error', `Failed to update race status: ${formatApiResponseError(res)}`);
        return;
      }

      setAutoExpandId(eid);
      await refreshEventDetails(eid);
      dispatchToast('toast-info', `${raceLabel} marked ${nextStatus}`);
    } catch (err: unknown) {
      dispatchToastError(err, 'Failed to update race status');
    } finally {
      setProgressingRaceKey(null);
    }
  }

  function handleSoftDeleteRace(eventId: string, raceId: string, raceName?: string): void {
    const eid = String(eventId ?? '').trim();
    const rid = String(raceId ?? '').trim();
    if (!eid || !rid || !window.eventsAPI) return;
    setConfirmState({
      kind: 'softDeleteRace',
      eventId: eid,
      raceId: rid,
      raceName: raceName?.trim() || undefined,
    });
  }

  function handleHardDeleteRace(eventId: string, raceId: string, raceName?: string): void {
    const eid = String(eventId ?? '').trim();
    const rid = String(raceId ?? '').trim();
    if (!eid || !rid || !window.eventsAPI) return;
    setConfirmState({
      kind: 'hardDeleteRace',
      eventId: eid,
      raceId: rid,
      raceName: raceName?.trim() || undefined,
    });
  }

  async function handleSoftDeleteEvent(eventId: string): Promise<void> {
    if (!window.eventsAPI) return;
    setConfirmState({ kind: 'softDeleteEvent', eventId });
  }

  async function handleHardDeleteEvent(eventId: string): Promise<void> {
    if (!window.eventsAPI) return;
    setConfirmState({ kind: 'hardDeleteEvent', eventId });
  }

  function handleOpenDevicePackaging(eventId: string, raceId: string): void {
    const rid = String(raceId ?? '').trim();
    if (!rid) return;
    setRaceWorkflowDialog({ eventId, raceId: rid, kind: 'devicePackaging' });
  }

  function handleOpenRecallDialog(eventId: string, raceId: string): void {
    setRecallDialogEventId(eventId);
    setRecallDialogRaceId(String(raceId ?? '').trim() || null);
    setRecallScanValue('');
    setRecallingDeviceId(null);
    if (!eventDetailsLoadedByEventId[eventId] && !refreshingDetailsByEventId[eventId]) {
      void refreshEventDetails(eventId);
    }
  }

  async function handleRemoveOperator(eventId: string, userId: string): Promise<void> {
    if (!window.eventsAPI) return;
    setConfirmState({ kind: 'removeOperator', eventId, userId });
  }

  async function handleSetDeviceRecalled(
    eventId: string,
    deviceId: string,
    recalled: 0 | 1,
    options?: { raceId?: string; retrievedPhase?: string },
  ): Promise<void> {
    if (!window.eventsAPI) return;
    const res = await window.eventsAPI.setEventDeviceRecalled(eventId, deviceId, recalled, {
      raceId: options?.raceId,
    });
    if (!res?.ok) {
      dispatchToast('toast-error', `Failed to update retrieval state: ${formatApiResponseError(res)}`);
      return;
    }
    setDetails((prev) => {
      const row = prev[eventId];
      if (!row) return prev;
      const devices = Array.isArray(row.devices) ? row.devices : [];
      const targetDeviceId = String(deviceId || '').trim();
      const explicitPhase = String(options?.retrievedPhase ?? '').trim().toUpperCase();
      const resolveRetrievedPhase = (device: (typeof devices)[number]): string | undefined => {
        if (explicitPhase) return explicitPhase;
        const rid = String(options?.raceId ?? device?.raceId ?? '').trim();
        if (!rid) return String(device?.retrievedPhase ?? '').trim().toUpperCase() || undefined;
        const race = (row.races ?? []).find((r) => String(r.raceId ?? '').trim() === rid);
        const st = normalizeRaceStatus(race?.status ?? '');
        if (st === 'DISTRIBUTION' || st === 'RACKING' || st === 'ACTIVE') return st;
        return String(device?.retrievedPhase ?? '').trim().toUpperCase() || undefined;
      };
      return {
        ...prev,
        [eventId]: {
          ...row,
          devices: devices.map((d) => {
            if (String(d?.deviceId || '').trim() !== targetDeviceId) return d;
            if (!recalled) {
              return {
                ...d,
                recalled,
                retrieved: recalled,
                retrievedPhase: undefined,
              };
            }
            return {
              ...d,
              recalled,
              retrieved: recalled,
              retrievedPhase: resolveRetrievedPhase(d),
            };
          }),
        },
      };
    });
    setEventDetailsRefreshVersionByEventId((prev) => ({
      ...prev,
      [eventId]: (prev[eventId] ?? 0) + 1,
    }));
  }

  const recallRaceLabel = useMemo(() => {
    if (!recallDialogEventId || !recallDialogRaceId) return null;
    const races = details[recallDialogEventId]?.races ?? [];
    const row = races.find((r) => String(r.raceId ?? '').trim() === recallDialogRaceId);
    return row?.name?.trim() || recallDialogRaceId;
  }, [details, recallDialogEventId, recallDialogRaceId]);

  const recallDevicesForScope = useMemo(() => {
    if (!recallDialogEventId) return [];
    let devices = details[recallDialogEventId]?.devices ?? [];
    const rid = String(recallDialogRaceId ?? '').trim();
    if (rid) {
      devices = devices.filter((d) => String(d.raceId ?? '').trim() === rid);
    }
    return devices;
  }, [details, recallDialogEventId, recallDialogRaceId]);

  const recallCandidates = useMemo(() => {
    return recallDevicesForScope
      .filter((d) => Number(d.recalled ?? 0) !== 1)
      .map((d) => String(d.deviceId || '').trim())
      .filter(Boolean);
  }, [recallDevicesForScope]);

  const recallTotalDevices = recallDevicesForScope.length;

  const raceWorkflowAssignmentsLocked = useMemo(() => {
    if (!raceWorkflowDialog) return false;
    const ev = events.find((e) => e.eventId === raceWorkflowDialog.eventId);
    if (isEventDeletedOnly(ev?.status)) return true;
    const rs = details[raceWorkflowDialog.eventId]?.races?.find(
      (r) => String(r.raceId ?? '').trim() === raceWorkflowDialog.raceId,
    )?.status;
    return isRaceAssignmentsLocked(rs);
  }, [raceWorkflowDialog, details, events]);

  const raceWorkflowRaceLabel = useMemo(() => {
    if (!raceWorkflowDialog) return null;
    const races = details[raceWorkflowDialog.eventId]?.races ?? [];
    const row = races.find((r) => String(r.raceId ?? '').trim() === raceWorkflowDialog.raceId);
    return row?.name?.trim() || raceWorkflowDialog.raceId;
  }, [details, raceWorkflowDialog]);

  const recallDoneCount = Math.max(0, recallTotalDevices - recallCandidates.length);
  const recallProgressPct = recallTotalDevices > 0 ? Math.round((recallDoneCount / recallTotalDevices) * 100) : 0;

  const markDeviceRecalledLocally = useCallback((eventId: string, deviceId: string): void => {
    const targetEventId = String(eventId || '').trim();
    const targetDeviceId = String(deviceId || '').trim();
    if (!targetEventId || !targetDeviceId) return;

    setDetails((prev) => {
      const row = prev[targetEventId];
      if (!row) return prev;
      const devices = Array.isArray(row.devices) ? row.devices : [];
      return {
        ...prev,
        [targetEventId]: {
          ...row,
          devices: devices.map((d) => (
            String(d?.deviceId || '').trim() === targetDeviceId
              ? { ...d, recalled: 1, retrieved: 1, retrievedPhase: 'ACTIVE' }
              : d
          )),
        },
      };
    });
  }, []);

  const flashRecallSubmitResult = useCallback((kind: 'success' | 'error'): void => {
    if (recallSubmitFlashTimerRef.current) {
      window.clearTimeout(recallSubmitFlashTimerRef.current);
    }
    setRecallSubmitFlash(kind);
    recallSubmitFlashTimerRef.current = window.setTimeout(() => {
      setRecallSubmitFlash(null);
      recallSubmitFlashTimerRef.current = null;
    }, 500);
  }, []);

  const handleRecallScanSubmit = useCallback(async (scannedRaw?: string): Promise<void> => {
    if (!window.eventsAPI || !recallDialogEventId) return;
    if (recallingDeviceId) return;
    const scanned = maybeConvertRacerangerScan(
      String(scannedRaw ?? recallScanValue ?? '').trim(),
    ).trim();
    if (!scanned) return;

    if (!recallCandidates.includes(scanned)) {
      dispatchToast(
        'toast-error',
        recallDialogRaceId
          ? `Device ${scanned} is not pending retrieval on this race`
          : `Device ${scanned} is not pending retrieval for this event`,
      );
      return;
    }

    setRecallingDeviceId(scanned);
    try {
      const res = await window.eventsAPI.setEventDeviceRecalled(recallDialogEventId, scanned, 1, {
        raceId: recallDialogRaceId ?? undefined,
      });
      if (!res?.ok) {
        dispatchToast('toast-error', `Failed to retrieve ${scanned}: ${formatApiResponseError(res)}`);
        flashRecallSubmitResult('error');
        return;
      }
      setRecallScanValue('');
      markDeviceRecalledLocally(recallDialogEventId, scanned);
      dispatchToast('toast-info', `Retrieved ${scanned}`);
      flashRecallSubmitResult('success');
      recallScanInputRef.current?.focus();
    } catch {
      flashRecallSubmitResult('error');
    } finally {
      setRecallingDeviceId(null);
    }
  }, [
    flashRecallSubmitResult,
    markDeviceRecalledLocally,
    recallCandidates,
    recallDialogEventId,
    recallDialogRaceId,
    recallScanValue,
    recallingDeviceId,
  ]);

  useEffect(() => {
    return () => {
      if (recallSubmitFlashTimerRef.current) {
        window.clearTimeout(recallSubmitFlashTimerRef.current);
      }
    };
  }, []);

  async function confirmAction(): Promise<void> {
    if (!confirmState) return;
    if (!window.eventsAPI) {
      setConfirmState(null);
      return;
    }

    setConfirmBusy(true);
    try {
      if (confirmState.kind === 'softDeleteEvent') {
        const res = await window.eventsAPI.deleteEvent(confirmState.eventId);
        if (!res?.ok) {
          dispatchToast('toast-error', `Failed to delete event: ${formatApiResponseError(res)}`);
          return;
        }
        dispatchToast('toast-info', `Event ${confirmState.eventId} marked as deleted`);
        patchEventRow(confirmState.eventId, { status: 'DELETED' });
        await refreshEventDetails(confirmState.eventId);
        setConfirmState(null);
        return;
      }

      if (confirmState.kind === 'hardDeleteEvent') {
        const res = await window.eventsAPI.deleteEvent(confirmState.eventId, { hard: true });
        if (!res?.ok) {
          dispatchToast('toast-error', `Failed to hard-delete event: ${formatApiResponseError(res)}`);
          return;
        }
        dispatchToast('toast-info', `Event ${confirmState.eventId} permanently deleted`);
        removeEventRowLocally(confirmState.eventId);
        setConfirmState(null);
        return;
      }

      if (confirmState.kind === 'removeOperator') {
        const res = await window.eventsAPI.removeEventOperators(confirmState.eventId, [confirmState.userId]);
        if (!res?.ok) {
          dispatchToast('toast-error', `Failed to remove operator: ${formatApiResponseError(res)}`);
          return;
        }
        dispatchToast('toast-info', `Removed operator ${confirmState.userId}`);
        await refreshEventDetails(confirmState.eventId);
        setConfirmState(null);
        return;
      }

      if (confirmState.kind === 'softDeleteRace') {
        const res = await window.eventsAPI.deleteEventRace(confirmState.eventId, confirmState.raceId);
        if (!res?.ok) {
          dispatchToast('toast-error', `Failed to soft-delete race: ${formatApiResponseError(res)}`);
          return;
        }
        dispatchToast(
          'toast-info',
          `Soft-deleted race ${confirmState.raceName ?? confirmState.raceId}`,
        );
        setAutoExpandId(confirmState.eventId);
        await refreshEventDetails(confirmState.eventId);
        setConfirmState(null);
        return;
      }

      if (confirmState.kind === 'hardDeleteRace') {
        const res = await window.eventsAPI.deleteEventRace(confirmState.eventId, confirmState.raceId, {
          hard: true,
        });
        if (!res?.ok) {
          dispatchToast('toast-error', `Failed to hard-delete race: ${formatApiResponseError(res)}`);
          return;
        }
        dispatchToast(
          'toast-info',
          `Hard-deleted race ${confirmState.raceName ?? confirmState.raceId}`,
        );
        setAutoExpandId(confirmState.eventId);
        await refreshEventDetails(confirmState.eventId);
        setConfirmState(null);
        return;
      }

      if (confirmState.kind === 'revertRaceStatus') {
        setProgressingRaceKey(`${confirmState.eventId}#${confirmState.raceId}`);
        try {
          const res = await window.eventsAPI.updateRaceStatus(
            confirmState.eventId,
            confirmState.raceId,
            confirmState.targetStatus,
          );
          if (!res?.ok) {
            dispatchToast('toast-error', `Failed to update race status: ${formatApiResponseError(res)}`);
            return;
          }
          setAutoExpandId(confirmState.eventId);
          await refreshEventDetails(confirmState.eventId);
          dispatchToast(
            'toast-info',
            `${confirmState.raceName ?? confirmState.raceId} moved to ${confirmState.targetStatus}`,
          );
          setConfirmState(null);
        } finally {
          setProgressingRaceKey(null);
        }
        return;
      }

      if (
        confirmState.kind === 'advanceRaceDraftToDevicePackaging'
        || confirmState.kind === 'advanceRacePackagingToDistribution'
        || confirmState.kind === 'advanceRaceDistributionToRacking'
        || confirmState.kind === 'advanceRaceRackingToActive'
        || confirmState.kind === 'advanceRaceCombinedDistributionToActive'
        || confirmState.kind === 'advanceRaceActiveToReporting'
        || confirmState.kind === 'advanceRaceReportingToComplete'
      ) {
        if (
          confirmState.kind === 'advanceRaceRackingToActive'
          && confirmState.checks.some((c) => !c.ok)
        ) {
          dispatchToast('toast-error', 'Resolve RACKING → ACTIVE checks before proceeding.');
          return;
        }
        setProgressingRaceKey(`${confirmState.eventId}#${confirmState.raceId}`);
        try {
          const res = await window.eventsAPI.updateRaceStatus(
            confirmState.eventId,
            confirmState.raceId,
            confirmState.nextStatus,
            undefined,
          );
          if (!res?.ok) {
            dispatchToast('toast-error', `Failed to update race status: ${formatApiResponseError(res)}`);
            return;
          }
          setAutoExpandId(confirmState.eventId);
          await refreshEventDetails(confirmState.eventId);
          dispatchToast(
            'toast-info',
            `${confirmState.raceName ?? confirmState.raceId} marked ${confirmState.nextStatus}`,
          );
          setConfirmState(null);
        } finally {
          setProgressingRaceKey(null);
        }
        return;
      }
    } finally {
      setConfirmBusy(false);
    }
  }

  function handleContinueAsOperator(): void {
    if (!operatorSelectedEventId) return;
    const selectedEvent = events.find((event) => event.eventId === operatorSelectedEventId);
    if (!selectedEvent) return;

    onOperatorEventContinue?.(selectedEvent);
    setOperatorSelectionConfirmed(true);
    setAutoExpandId(operatorSelectedEventId);
    navigate('/');
  }

  function handleNavigateToDevicesFromEvent(payload: {
    eventId: string;
    eventName?: string | null;
    raceId?: string | null;
    unretrievedOnly?: boolean;
  }): void {
    const eventId = String(payload.eventId ?? '').trim();
    if (!eventId) return;
    if (isAdmin) {
      const sp = new URLSearchParams();
      sp.set('eventId', eventId);
      const rid = String(payload.raceId ?? '').trim();
      if (rid) sp.set('raceId', rid);
      if (payload.unretrievedOnly) sp.set('unretrievedOnly', '1');
      navigate({ pathname: '/', search: `?${sp.toString()}` });
      return;
    }
    if (isOperator && !isAdmin) {
      onOperatorNavigateToDevices?.(payload);
    }
  }

  return (
    <div className="h-full min-h-0 flex flex-col bg-slate-100 dark:bg-slate-800">
      <div className={PAGE_TAB_HEADER}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold">Events overview</div>
            <div className="text-sm text-gray-500 dark:text-slate-400">
              Filter, browse, and open event details.
            </div>
          </div>

          <RefreshButton
            busy={loading}
            onClick={() => void loadAll()}
            disabled={loading}
            className={BTN_SECONDARY}
            busyText="Refreshing..."
            busyClassName="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 p-4 overflow-auto">
        <div className="flex flex-col gap-4">
          {isOperator && !isAdmin && !operatorSelectionConfirmed ? (
            <section className="rounded-xl border border-gray-200 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
              <header className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">Operator access</div>
                  <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                    Review your assigned events, expand details if needed, then continue with the selected one.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleContinueAsOperator}
                  disabled={!operatorSelectedEventId}
                  className={
                    operatorSelectedEventId
                      ? `${BTN_PRIMARY} animate-pulse shadow-lg shadow-blue-200 ring-2 ring-blue-200`
                      : BTN_SECONDARY
                  }
                >
                  Continue
                </button>
              </header>

              <div className="p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200">
                    <span className="text-gray-500 dark:text-slate-400">User</span>
                    <span className="font-semibold text-gray-900 dark:text-slate-100">{userName}</span>
                  </div>
                  <div className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200">
                    <span className="text-gray-500 dark:text-slate-400">Assigned events</span>
                    <span className="font-semibold text-gray-900 dark:text-slate-100">{events.length}</span>
                  </div>
                  {operatorSelectedEventId ? (
                    <div className="inline-flex h-9 items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-3 text-sm text-blue-900 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-100">
                      <span className="text-blue-700 dark:text-blue-200">Selected</span>
                      <span className="font-semibold">{operatorSelectedEventLabel || operatorSelectedEventId}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}

          <div className="flex-1 min-h-0 flex gap-4">
            <EventsOverviewMainPanel
              loading={loading}
              filteredEvents={visibleEvents}
              totalEvents={events.length}
              filters={filters}
              onChangeFilters={setFilters}
              activeFilterCount={activeFilterCount}
              details={details}
              expanded={expanded}
              globalUserIdToDisplayName={globalUserIdToDisplayName}
              isAdmin={isAdmin}
              isOperator={isOperator}
              canCreateEvent={isAdmin || isOperator}
              selectedEventId={isOperator && !isAdmin ? operatorSelectedEventId : null}
              onSelectEvent={
                isOperator && !isAdmin && !operatorSelectionConfirmed
                  ? setOperatorSelectedEventId
                  : undefined
              }
              progressingRaceKey={progressingRaceKey}
              onAdvanceRace={(eid, race, ns, options) => {
                void handleAdvanceRace(eid, race, ns, options);
              }}
              onSoftDeleteRace={isAdmin ? handleSoftDeleteRace : undefined}
              onHardDeleteRace={isAdmin ? handleHardDeleteRace : undefined}
              onCreateEvent={() => setCreateDialogOpen(true)}
              onToggleExpanded={toggleExpanded}
              onOpenAssignOperatorDialog={openAssignOperatorDialog}
              onOpenDevicePackaging={isAdmin || isOperator ? handleOpenDevicePackaging : undefined}
              onOpenRecallDialog={isAdmin ? handleOpenRecallDialog : undefined}
              onSoftDeleteEvent={handleSoftDeleteEvent}
              onHardDeleteEvent={handleHardDeleteEvent}
              onRemoveOperator={handleRemoveOperator}
              onSetDeviceRecalled={handleSetDeviceRecalled}
              onRefreshEventDetails={refreshEventDetails}
              refreshingDetailsByEventId={refreshingDetailsByEventId}
              eventDetailsRefreshVersionByEventId={eventDetailsRefreshVersionByEventId}
              eventDetailsLoadedByEventId={eventDetailsLoadedByEventId}
              onNavigateToDevices={
                isAdmin || (isOperator && !isAdmin) ? handleNavigateToDevicesFromEvent : undefined
              }
            />
          </div>
        </div>
      </div>

      <CreateEventDialog
        open={createDialogOpen}
        form={createForm}
        wtEvents={wtEvents}
        wtLoading={wtEventsLoading}
        creatingEvent={creatingEvent}
        onClose={() => setCreateDialogOpen(false)}
        onChange={(e) => {
          const { name, value } = e.target;
          setCreateForm((prev) => ({
            ...prev,
            [name]: value,
          }));
        }}
        onRefreshWtEvents={() => {
          void loadWorldTriathlonEvents();
        }}
        onSubmit={handleCreateEventSubmit}
      />

      <AssignOperatorDialog
        open={!!assignOperatorDialogEventId}
        eventId={assignOperatorDialogEventId}
        events={events}
        assignedOperators={
          assignOperatorDialogEventId ? (details[assignOperatorDialogEventId]?.operators ?? []) : []
        }
        availableUsers={availableUsers}
        selectedUserId={selectedUserId}
        selectedRole={selectedRole}
        assigningOperator={assigningOperator}
        onClose={() => setAssignOperatorDialogEventId(null)}
        onUserChange={setSelectedUserId}
        onRoleChange={setSelectedRole}
        onAssign={handleAssignOperator}
      />

      <OperatorIntroDialog
        open={operatorIntroOpen}
        userName={userName}
        onClose={() => setOperatorIntroOpen(false)}
      />

      {raceWorkflowDialog ? (
        <PortaledLayer>
          <div
            data-race-stacked-overlay=""
            className="fixed inset-0 z-[5100] flex items-center justify-center bg-black/40 p-4 dark:bg-black/70"
          >
            <div className="max-h-[92vh] w-full max-w-[1200px] overflow-visible rounded-xl border border-gray-200 bg-white text-gray-800 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
              <div className="flex items-center justify-between gap-3 rounded-t-xl border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
                <div className="text-sm font-semibold text-gray-900 dark:text-slate-100 min-w-0">
                  Upload Device to Athlete assignments
                  {raceWorkflowRaceLabel ? (
                    <span className="block text-xs font-normal text-gray-500 dark:text-slate-400 mt-0.5">
                      Race: {raceWorkflowRaceLabel}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => setRaceWorkflowDialog(null)}
                  className={BTN_ICON}
                  aria-label="Close"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="max-h-[calc(92vh-56px)] overflow-y-auto overflow-x-visible rounded-b-xl bg-white p-4 dark:bg-slate-900">
                <DevicePackageAssignmentDialog
                  eventId={raceWorkflowDialog.eventId}
                  raceId={raceWorkflowDialog.raceId}
                  raceName={raceWorkflowRaceLabel ?? undefined}
                  assignmentsLocked={raceWorkflowAssignmentsLocked}
                  onSaved={(id) => void refreshEventDetails(id)}
                />
              </div>
            </div>
          </div>
        </PortaledLayer>
      ) : null}

      {recallDialogEventId ? (
        <PortaledLayer>
          <div
            data-race-stacked-overlay=""
            className="fixed inset-0 z-[5100] flex items-center justify-center bg-black/40 p-4 dark:bg-black/70"
          >
            <div className="max-h-[85vh] w-full max-w-[720px] overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-800 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
              <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
                <div className="text-sm font-semibold text-gray-900 dark:text-slate-100 min-w-0">
                  Retrieve device
                  {recallRaceLabel ? (
                    <span className="block text-xs font-normal text-gray-500 dark:text-slate-400 mt-0.5">
                      Race: {recallRaceLabel}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setRecallDialogEventId(null);
                    setRecallDialogRaceId(null);
                    setRecallScanValue('');
                    setRecallingDeviceId(null);
                    setRecallSubmitFlash(null);
                  }}
                  className={BTN_ICON}
                  aria-label="Close retrieval dialog"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-3 bg-white p-4 dark:bg-slate-900">
                <div className="text-xs text-gray-600 dark:text-slate-400">
                  {recallScannerConnected
                    ? `Scanner connected${recallScannerPort?.path ? ` (${recallScannerPort.path})` : ''}. Scan a device barcode to record retrieval one-by-one.`
                    : 'Waiting for scanner connection'}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-slate-400">
                    <span>
                      Progress: <span className="font-semibold text-gray-800 dark:text-slate-200">{recallDoneCount}</span> / {recallTotalDevices}
                    </span>
                    <span className="font-semibold text-gray-800 dark:text-slate-200">{recallProgressPct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${recallProgressPct}%` }}
                    />
                  </div>
                </div>
                <BarcodeScannerEntryField
                  inputRef={recallScanInputRef}
                  autoFocus
                  connected={recallScannerConnected}
                  value={recallScanValue}
                  onChange={setRecallScanValue}
                  onSubmit={(value) => { void handleRecallScanSubmit(value); }}
                  disabled={!!recallingDeviceId}
                  loading={!!recallingDeviceId}
                  mode={recallEntryMode}
                  onModeChange={(next) => {
                    setRecallEntryMode(next);
                    if (next === 'manual') setRecallScanValue('');
                  }}
                  onFocus={claimRecallScanner}
                  parseValue={maybeConvertRacerangerScan}
                />
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500 dark:text-slate-400">
                    Remaining devices pending retrieval: <span className="font-semibold text-gray-700 dark:text-slate-300">{recallCandidates.length}</span>
                  </div>
                  <button
                    type="button"
                    disabled={!!recallingDeviceId || !!recallSubmitFlash || !recallScanValue.trim()}
                    onClick={() => void handleRecallScanSubmit(recallScanValue)}
                    className={`${BTN_SECONDARY} inline-flex items-center gap-2`}
                  >
                    {recallingDeviceId ? (
                      <>
                        <InlineSpinner />
                        {`Retrieving ${recallingDeviceId}...`}
                      </>
                    ) : recallSubmitFlash === 'success' ? (
                      <span className="text-green-700 font-semibold">✓</span>
                    ) : recallSubmitFlash === 'error' ? (
                      <span className="text-red-700 font-semibold">✕</span>
                    ) : 'Retrieve scanned'}
                  </button>
                </div>
                <div className="max-h-64 overflow-auto rounded border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                  {recallCandidates.length ? (
                    <ul className="divide-y divide-gray-100 dark:divide-slate-800">
                      {recallCandidates.map((deviceId) => (
                        <li key={deviceId} className="px-3 py-2 text-xs text-gray-700 dark:text-slate-300 font-mono break-all">
                          {deviceId}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="px-3 py-4 text-xs text-gray-500 dark:text-slate-400">All devices are already retrieved.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </PortaledLayer>
      ) : null}


      <DraftToDevicePackagingConfirmDialog
        open={confirmState?.kind === 'advanceRaceDraftToDevicePackaging'}
        eventId={
          confirmState?.kind === 'advanceRaceDraftToDevicePackaging' ? confirmState.eventId : ''
        }
        eventName={
          confirmState?.kind === 'advanceRaceDraftToDevicePackaging' ? confirmState.eventName : undefined
        }
        subtitleOverride={
          confirmState?.kind === 'advanceRaceDraftToDevicePackaging'
            ? confirmState.raceName || confirmState.raceId
            : undefined
        }
        busy={confirmBusy}
        onCancel={() => setConfirmState(null)}
        onProceed={() => void confirmAction()}
      />

      <DevicePackagingToDistributionConfirmDialog
        open={confirmState?.kind === 'advanceRacePackagingToDistribution'}
        eventId={
          confirmState?.kind === 'advanceRacePackagingToDistribution' ? confirmState.eventId : ''
        }
        eventName={
          confirmState?.kind === 'advanceRacePackagingToDistribution'
            ? confirmState.eventName
            : undefined
        }
        restrictToRaceId={
          confirmState?.kind === 'advanceRacePackagingToDistribution' ? confirmState.raceId : null
        }
        subtitleOverride={
          confirmState?.kind === 'advanceRacePackagingToDistribution'
            ? confirmState.raceName || confirmState.raceId
            : undefined
        }
        busy={confirmBusy}
        onCancel={() => setConfirmState(null)}
        onProceed={() => void confirmAction()}
      />

      <ActiveToReportingConfirmDialog
        open={confirmState?.kind === 'advanceRaceActiveToReporting'}
        eventId={confirmState?.kind === 'advanceRaceActiveToReporting' ? confirmState.eventId : ''}
        eventName={confirmState?.kind === 'advanceRaceActiveToReporting' ? confirmState.eventName : undefined}
        subtitleOverride={
          confirmState?.kind === 'advanceRaceActiveToReporting'
            ? confirmState.raceName || confirmState.raceId
            : undefined
        }
        busy={confirmBusy}
        onCancel={() => setConfirmState(null)}
        onProceed={() => void confirmAction()}
      />

      <ReportingToCompleteConfirmDialog
        open={confirmState?.kind === 'advanceRaceReportingToComplete'}
        eventId={confirmState?.kind === 'advanceRaceReportingToComplete' ? confirmState.eventId : ''}
        raceId={confirmState?.kind === 'advanceRaceReportingToComplete' ? confirmState.raceId : ''}
        eventName={confirmState?.kind === 'advanceRaceReportingToComplete' ? confirmState.eventName : undefined}
        subtitleOverride={
          confirmState?.kind === 'advanceRaceReportingToComplete'
            ? confirmState.raceName || confirmState.raceId
            : undefined
        }
        nonRecalledDeviceIds={
          confirmState?.kind === 'advanceRaceReportingToComplete' ? confirmState.nonRecalledDeviceIds : []
        }
        busy={confirmBusy}
        onCancel={() => setConfirmState(null)}
        onProceed={() => void confirmAction()}
      />

      <DistributionToRackingConfirmDialog
        open={confirmState?.kind === 'advanceRaceDistributionToRacking'}
        eventId={
          confirmState?.kind === 'advanceRaceDistributionToRacking' ? confirmState.eventId : ''
        }
        eventName={
          confirmState?.kind === 'advanceRaceDistributionToRacking' ? confirmState.eventName : undefined
        }
        subtitleOverride={
          confirmState?.kind === 'advanceRaceDistributionToRacking'
            ? confirmState.raceName || confirmState.raceId
            : undefined
        }
        busy={confirmBusy}
        onCancel={() => setConfirmState(null)}
        onProceed={() => void confirmAction()}
      />

      <CombinedDistributionToActiveConfirmDialog
        open={confirmState?.kind === 'advanceRaceCombinedDistributionToActive'}
        eventId={
          confirmState?.kind === 'advanceRaceCombinedDistributionToActive' ? confirmState.eventId : ''
        }
        eventName={
          confirmState?.kind === 'advanceRaceCombinedDistributionToActive' ? confirmState.eventName : undefined
        }
        subtitleOverride={
          confirmState?.kind === 'advanceRaceCombinedDistributionToActive'
            ? confirmState.raceName
            : undefined
        }
        rackedDeviceIds={
          confirmState?.kind === 'advanceRaceCombinedDistributionToActive' ? confirmState.rackedDeviceIds : []
        }
        retrievalDeviceIds={
          confirmState?.kind === 'advanceRaceCombinedDistributionToActive'
            ? confirmState.retrievalDeviceIds
            : []
        }
        releasedDeviceIds={
          confirmState?.kind === 'advanceRaceCombinedDistributionToActive'
            ? confirmState.releasedDeviceIds
            : []
        }
        busy={confirmBusy}
        onCancel={() => setConfirmState(null)}
        onProceed={() => void confirmAction()}
      />
      <RackingToActiveConfirmDialog
        open={confirmState?.kind === 'advanceRaceRackingToActive'}
        eventId={confirmState?.kind === 'advanceRaceRackingToActive' ? confirmState.eventId : ''}
        eventName={
          confirmState?.kind === 'advanceRaceRackingToActive' ? confirmState.eventName : undefined
        }
        subtitleOverride={
          confirmState?.kind === 'advanceRaceRackingToActive'
            ? confirmState.raceName || confirmState.raceId
            : undefined
        }
        checks={confirmState?.kind === 'advanceRaceRackingToActive' ? confirmState.checks : []}
        busy={confirmBusy}
        onCancel={() => setConfirmState(null)}
        onProceed={() => void confirmAction()}
      />

      <ConfirmDialog
        open={confirmState?.kind === 'revertRaceStatus'}
        danger={false}
        busy={confirmBusy}
        stackedAboveRacePortal
        title={
          confirmState?.kind === 'revertRaceStatus'
            ? formatMoveToStatusLabel('backward', confirmState.targetStatus)
            : ''
        }
        message={
          confirmState?.kind === 'revertRaceStatus' ? (
            <>
              <p>
                Move <span className="font-semibold">{confirmState.raceName ?? confirmState.raceId}</span> from{' '}
                <span className="font-semibold">{confirmState.currentStatus}</span> back to{' '}
                <span className="font-semibold">{confirmState.targetStatus}</span>?
              </p>
            </>
          ) : null
        }
        confirmText={
          confirmState?.kind === 'revertRaceStatus'
            ? formatMoveToStatusLabel('backward', confirmState.targetStatus)
            : 'Confirm'
        }
        onCancel={() => setConfirmState(null)}
        onConfirm={() => void confirmAction()}
      />

      <ConfirmDialog
        open={
          !!confirmState &&
          confirmState.kind !== 'advanceRaceDraftToDevicePackaging' &&
          confirmState.kind !== 'advanceRacePackagingToDistribution' &&
          confirmState.kind !== 'advanceRaceDistributionToRacking' &&
          confirmState.kind !== 'advanceRaceRackingToActive' &&
          confirmState.kind !== 'advanceRaceCombinedDistributionToActive' &&
          confirmState.kind !== 'advanceRaceActiveToReporting' &&
          confirmState.kind !== 'advanceRaceReportingToComplete' &&
          confirmState.kind !== 'revertRaceStatus'
        }
        danger
        busy={confirmBusy}
        title={
          confirmState?.kind === 'softDeleteEvent'
            ? 'Confirm soft-delete'
            : confirmState?.kind === 'hardDeleteEvent'
              ? 'Confirm hard-delete'
              : confirmState?.kind === 'removeOperator'
                ? 'Confirm operator removal'
                : confirmState?.kind === 'softDeleteRace'
                  ? 'Confirm soft-delete'
                  : confirmState?.kind === 'hardDeleteRace'
                    ? 'Confirm hard-delete'
                    : ''
        }
        message={
          confirmState?.kind === 'softDeleteEvent' ? (
            <>
              Soft-delete event <span className="font-semibold">{confirmState.eventId}</span>? This sets status
              to <span className="font-semibold">DELETED</span> and keeps history.
            </>
          ) : confirmState?.kind === 'hardDeleteEvent' ? (
            <>
              Permanently delete event <span className="font-semibold">{confirmState.eventId}</span>? This removes
              the event, <span className="font-semibold">all races</span> under it, race and operator assignments,
              related file metadata and storage, and cannot be undone.
            </>
          ) : confirmState?.kind === 'removeOperator' ? (
            <>
              Remove operator <span className="font-semibold">{confirmState.userId}</span> from event{' '}
              <span className="font-semibold">{confirmState.eventId}</span>?
            </>
          ) : confirmState?.kind === 'softDeleteRace' ? (
            <>
              Soft-delete race{' '}
              <span className="font-semibold">{confirmState.raceName ?? confirmState.raceId}</span> on event{' '}
              <span className="font-semibold">{confirmState.eventId}</span>? This sets the race status to{' '}
              <span className="font-semibold">DELETED</span> and may archive assignment history for that race.
            </>
          ) : confirmState?.kind === 'hardDeleteRace' ? (
            <>
              Hard-delete race{' '}
              <span className="font-semibold">{confirmState.raceName ?? confirmState.raceId}</span> on event{' '}
              <span className="font-semibold">{confirmState.eventId}</span>? This removes the race record, related
              assignment history, and race files from storage and cannot be undone.
            </>
          ) : null
        }
        confirmText={
          confirmState?.kind === 'softDeleteEvent'
            ? 'Soft-delete'
            : confirmState?.kind === 'hardDeleteEvent'
              ? 'Hard-delete'
              : confirmState?.kind === 'removeOperator'
                ? 'Remove operator'
                : confirmState?.kind === 'softDeleteRace'
                  ? 'Soft-delete'
                  : confirmState?.kind === 'hardDeleteRace'
                    ? 'Hard-delete'
                    : 'Confirm'
        }
        onConfirm={() => void confirmAction()}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
}
